import _ from 'lodash';
import moment from 'moment';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Subject } from "rxjs/Subject";
import {map, concatMap, takeUntil, switchMap, catchError, finalize, take} from "rxjs/operators";

import { AttachmentService } from "@ispm/migration/services/attachment.service";
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { BasicModalComponent } from '@ispm/layout/components/modals/basic-modal/basic-modal.component';
import { CollectionFormComponent } from "@ispm/test/components/collection-form/collection-form.component";
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { DashboardService } from "../../../worklist/services/dashboard.service";
import { DiagnosticOrderActionsService } from "@ispm/test/services/diagnostic-order-actions.service";
import { DocumentsTabService } from "@ispm/test/services/documents-tab.service";
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { DynamicallyAddComponentService } from "@ispm/shared/services/dynamically-add-component.service";
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PollJobService } from '@ispm/dashboard/test/services/poll-job.service';
import { RelatedControlTest } from "../../../quality-check/components/qc-results/qc-results.component";
import { ReleaseReportComponent } from './release-report/release-report.component';
import { ReportPreviewService } from '@ispm/therapies/services/report-preview.service';
import { ResourceSearchType } from "@ispm/test/components/documents-tab/interfaces/document-test-data.interface";
import { RouterManagerService } from "@ispm/core/services/router-manager.service";
import { TestInfoComponent } from "@ispm/test/components/test-info/test-info.component";
import { TestService } from '@ispm/dashboard/test/services/test.service';
import { TestSummaryActionsService } from '@ispm/dashboard/test/services/test-summary-actions.service';
import { TherapyMatchComponent } from "@ispm/migration/components/therapyMatch/therapy-match.component";
import { TreatmentsComponent } from "@ispm/therapies/components/treatments/treatments.component";
import { WorkflowConfigService } from "@ispm/workflow/services/workflow-config.service";
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import {of} from "rxjs/observable/of";
import { ReleaseWarningComponent } from "@ispm/therapies/components/report-preview/release-warning/release-warning.component";

const BORDER = 'border: 1px solid #FFDB1F;';
const EDITABLE_VIEW_ATTRIBUTE = 'editableView';
const FORM_NAME_ATTRIBUTE = 'formKey';
const FORM_ID_ATTRIBUTE = 'formId';
const INDEX_ATTRIBUTE = 'index';
const ITEM_TYPE = 'itemType';
const ITEM_CONFIG = 'config';
const RELEVANT_RESOURCE_NAME_ATTRIBUTE = 'relevantResourceName';
const RESOURCE_NAME_ATTRIBUTE = 'resourcename';
const PROPERTY_NAME_ATTRIBUTE = 'propertyName';
const PROPERTY_KEY_ATTRIBUTE = 'propertyKey';
const PROPERTY_PATH_ATTRIBUTE = 'propertyPath';

@Component({
  selector: 'gen-report-preview',
  templateUrl: './report-preview.component.html',
  styleUrls: ['./report-preview.component.scss'],
})
export class ReportPreviewComponent implements OnInit, OnDestroy {
  @ViewChild('renderedReportDiv', { static: false }) renderedReportDiv: ElementRef;
  @ViewChild('editForm', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;

  private addendumText: string = '';
  private analysis: any;
  private componentsToAddDynamically = [
    { key: 'testInfo', value: TestInfoComponent},
    { key: 'therapyPriority', value: TreatmentsComponent},
    { key: 'therapyMatch', value: TherapyMatchComponent},
    { key: 'dataCollection', value: CollectionFormComponent }
  ];
  private currentRevision: any;
  private currentRevisionId: string;
  private currentUserId: any;
  private destroyed$: Subject<any> = new Subject();
  private interpretationText: string = '';
  private modalSubscriber$: Subscription;
  private postAddendumReportToEmrEnabled: boolean;
  private postFinalReportToEmrEnabled: boolean;
  private qcWarningConfig: any;
  private reportTemplatesExist: boolean;
  private testNumber: any;
  private testParams: any;
  private timer: any;
  private updatedDataTable: any = null;
  private xHtmlReportTemplate: any;

  public editReportToggle = false;
  public editableView = '';
  public isAddendumReportEnabled = false;
  public isEditableReport: boolean = false;
  public loading: boolean = false;
  public payload: { config: any, formId: string, resource: any } = { config: { }, formId: '', resource: { } };
  public revisionDetails: any;
  public stopAction: boolean = false;
  public submitted: boolean;

  constructor(
    private attachmentsService: AttachmentService,
    private authService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private coreActionCreatorService: CoreActionCreatorService,
    private dashboardService: DashboardService,
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private documentsTabService: DocumentsTabService,
    private dynamicallyAddComponentService: DynamicallyAddComponentService,
    private modalLoader: DynamicModalLoaderService,
    private notificationMessageService: NotificationMessageService,
    private pollJobService: PollJobService,
    private reportPreviewService: ReportPreviewService,
    private routerManagerService: RouterManagerService,
    private testService: TestService,
    private testSummaryActionsService: TestSummaryActionsService,
    private workflowConfigService: WorkflowConfigService,
    private workflowService: WorkflowService,
  ) {
  }

  public ngOnInit(): void {
    this.dynamicallyAddComponentService.setRootViewContainerRef(this.viewContainerRef);
    this.qcWarningConfig = this.testService.testConfiguration.qcWarningConfig;
    this.testParams = this.routerManagerService.parameters;
    this.currentRevisionId = this.testParams.revisionId;
    this.currentUserId = _.get(this.authService, 'user.info.id', null);

    this.removeFlagForProceedTestStage();
    this.init(this.testService);

    if (this.currentRevision) {
      this.isEditableReport = this.editReportStatus();
      this.stopAction = this.stopActionStatus();
      this.isAddendumReportEnabled = _.get(this.currentRevision[0], 'finalReportDoneForStopAction', false)
        ? true
        : this.addendumReportStatus();
    }

    if (this.testService.testConfiguration.xHtmlReportTemplates.length === 0) {
      this.reportTemplatesExist = false;
    } else {
      const reportTemplate = this.testService.testConfiguration.xHtmlReportTemplates.filter((reportTemplate: any) => {
        if (reportTemplate.type === 'final') {
          return reportTemplate;
        }
      });
      this.reportTemplatesExist = !!reportTemplate.length;
    }

    // Check if FinalReport\AddendumReport SHALL be posted to EMR
    const effectiveTemplates = this.testService?.testConfiguration?.taskDispatchTemplates?.filter(
      (e) => e
        && e.type
        && e.type === 'postReportToEMR'
        && e.reference
        && e.settings
        && e.settings.enabled
    );

    if (effectiveTemplates && effectiveTemplates.length > 0) {
      this.postFinalReportToEmrEnabled = effectiveTemplates.filter((e: any) => e.settings.releaseFinal).length > 0;
      this.postAddendumReportToEmrEnabled = effectiveTemplates.filter((e: any) => e.settings.addAddendum).length > 0;
    }

    this.setPrefilledData();
    this.loadRenderedReport();
  }

  public ngOnDestroy(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public isPerformerUserLoggedIn(): boolean {
    return this.testService.patientTests ? this.testService.patientTests.current.performer === this.currentUserId : false;
  }

  public handleHover(): void {
    this.activateMouseEvents(this.editReportToggle);
    this.cdr.markForCheck();
  }

  public releaseFinalReport(): boolean {
    const qcWarningConfig = this.testService.testConfiguration.qcWarningConfig;

    if (!qcWarningConfig) {
      this.onReleaseFinalReport();

      return true;
    } else {
      this.reportPreviewService.getControlSampleStatuses()
        .pipe(
          concatMap((controlSampleStatus: any) =>
            this.reportPreviewService.getRelatedControlTests(this.testParams.testId, this.testService.testConfiguration.id)
              .pipe(
                map((relatedTests: any) => {
                  const showWarning = relatedTests && relatedTests.length > 0
                    ? this.analyseRelatedControlTestStatus(relatedTests[0], controlSampleStatus) : false;
                  return { relatedTests, controlSampleStatus, showWarning };
                }),
                catchError(error => {
                  console.error('Error while getting related control tests - ' + error.message);
                  return of({ relatedTests: [], controlSampleStatus, showWarning: false });
                })
              )
          ),
          takeUntil(this.destroyed$.asObservable())
        )
        .subscribe(
          (data) => {
            if (data.showWarning) {
              this.modalSubscriber$ = this.modalLoader.popupModal(
                PlaceholderModalComponent,
                {
                  title: qcWarningConfig.title,
                  componentToLoad: ReleaseWarningComponent,
                  content: qcWarningConfig.content,
                  buttons: {
                    approveBtn: qcWarningConfig.approveBtn,
                    declineBtn: qcWarningConfig.declineBtn
                  },
                  payload: { },
                  approveCallback: () => {
                    this.onReleaseFinalReport();
                    return of(true);
                  },
                },
                qcWarningConfig.dialogType
              )
                .pipe(
                  map(response => response.payload),
                )
                .subscribe(
                  () => { /* handle success if needed */ },
                  error => {
                    console.error('Error while showing the warning modal - ' + error.message);
                  },
                  () => {
                    this.modalSubscriber$.unsubscribe();
                    this.modalSubscriber$ = null;
                  }
                );
            } else {
              this.onReleaseFinalReport();
            }
          },
          (error) => {
            console.error('Error while processing - ' + error.message);
          }
        );
    }

    return false;
  }

  public isProceedFinalReportButtonDisabled(): boolean {
    const proceedData = JSON.parse(localStorage.getItem('test-report-proceed')) || [];

    return proceedData.includes(this.testParams.revisionId);
  }

  public releaseFinalReportForStopAction(): void {
    if (!!this.testService.testConfiguration?.skipInterpretationSummary) {
      this.releaseFinalReportModal('', true);
    } else {
      this.modalSubscriber$ = this.modalLoader.popupModal(
        PlaceholderModalComponent,
        {
          title: 'Add interpretation summary',
          componentToLoad: ReleaseReportComponent,
          buttons: {
            approveBtn: 'Yes',
            declineBtn: 'Cancel'
          },
          payload: { },
          approveCallback: (summaryText: string) => {
            this.releaseFinalReportModal(summaryText, true);
            return Observable.of(true);
          },
        },
        ModalTypes.NONE
      )
        .pipe(
          map(response => response.payload)
        )
        .finally(() => {
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        })
        .subscribe();
    }
  }

  public releaseAddendumReport(): void {
    this.modalSubscriber$ = this.modalLoader.popupModal(
      PlaceholderModalComponent,
      {
        title: '',
        componentToLoad: ReleaseReportComponent,
        buttons: {
          approveBtn: 'Release',
          declineBtn: 'Cancel'
        },
        buttonsStatuses: {
          disableApproveBtnOnEmptyPayload: true
        },
        payload: {
          text: this.addendumText
        },
        approveCallback: (summaryText: string) => {
          this.addendumText = summaryText;
          this.releaseAddendumReportModal(summaryText);
          return Observable.of(true);
        },
      },
      ModalTypes.CUSTOM
    )
      .pipe(
        map(response => response.payload)
      )
      .finally(() => {
        this.addendumText = '';
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      })
      .subscribe();
  }

  public updateReport() {
    if (this.editableView === 'dataCollection') {
      if (!_.isEmpty(this.updatedDataTable.data.formData)) {
        _.set(
          this.updatedDataTable.originalData,
          this.updatedDataTable.propertyPath,
          this.updatedDataTable.data.formData
        );
      }

      if (!!_.get(this.updatedDataTable.originalData, this.updatedDataTable.propertyName, false) === false) {
        this.hideModal();

        return;
      }

      this.updatedDataTable.originalData[this.updatedDataTable.propertyName].map((resourceName: any) => {
        try {
          if (resourceName.name === this.updatedDataTable.propertyKey) {
            resourceName.value = JSON.stringify(resourceName.value);
          }
        } catch (e) {
        }
      });

      const testData: any = {
        currentTest: this.analysis
      };

      this.loading = true;
      this.cdr.markForCheck();

      this.documentsTabService
        .updateResource(
          this.updatedDataTable.resourceName,
          this.updatedDataTable.originalData,
          testData,
          false
        )
        .pipe(
          takeUntil(this.destroyed$.asObservable())
        )
        .finally(() => this.hideModal())
        .subscribe(() => this.loadRenderedReport());
    } else {
      // store updated resource of dynamic-form
      this.upsertResource()
        .pipe(
          takeUntil(this.destroyed$.asObservable())
        )
        .finally(() => this.hideModal())
        .subscribe(() => this.loadRenderedReport());
    }
  }

  public dismiss(force = false){
    this.hideModal(force);
  }

  public nestedComponentLoaded() {
    this.loading = false
  }

  public updateResource(source, target) {
    Object.assign(source, target); // updating the this.payload.resource of newest values
  }

  public handleClick($event: MouseEvent) {
    if (!this.editReportToggle) {
      return;
    }

    const elem = this.getEditableAncestor($event.target);

    if (!elem) {
      return;
    }

    const configString = elem.getAttribute(ITEM_CONFIG);
    const formId = elem.getAttribute(FORM_ID_ATTRIBUTE);
    const formName = elem.getAttribute(FORM_NAME_ATTRIBUTE);
    const itemType = elem.getAttribute(ITEM_TYPE);
    const propertyKey = elem.getAttribute(PROPERTY_KEY_ATTRIBUTE);
    const propertyName = elem.getAttribute(PROPERTY_NAME_ATTRIBUTE);
    const propertyPath = elem.getAttribute(PROPERTY_PATH_ATTRIBUTE);
    const relevantResourceName = elem.getAttribute(RELEVANT_RESOURCE_NAME_ATTRIBUTE);
    const testData: any = {
      revisionId: this.currentRevisionId,
      currentTest: this.analysis,
      resourceId: elem.getAttribute(INDEX_ATTRIBUTE)
    };

    if (itemType?.toUpperCase() === 'DYNAMICFORM') {
      const config = {};

      if (configString) {
        configString.split(',').forEach((conf: string) => {
          const confArr = conf.split(':');

          if (confArr.length > 1) {
            config[confArr[0]] = confArr[1];
          }
        });
      }

      this.testService.getDiagnosticSummaryByRevision(this.currentRevision[0].id)
        .pipe(
          takeUntil(this.destroyed$.asObservable())
        )
        .subscribe(
          (records) => {
            this.payload = {config: config, formId: formId, resource: records[0] || {}}
          },
          () => {
            this.payload = {config: config, formId: formId, resource: {}}
          },
          () => {
            this.payload.resource.resourceType = elem.getAttribute(RESOURCE_NAME_ATTRIBUTE);
            document.getElementById('modalContainer').style.display = 'block';
            this.editableView = elem.getAttribute(EDITABLE_VIEW_ATTRIBUTE);
            this.viewContainerRef.clear();
            this.cdr.detectChanges();
          }
        );
    }
    else if(relevantResourceName) {
      this.documentsTabService
        .findResource(relevantResourceName, ResourceSearchType.BY_ID, testData)
        .pipe(
          takeUntil(this.destroyed$.asObservable())
        )
        .subscribe((receivedData: any) => {
          this.editableView = elem.getAttribute(EDITABLE_VIEW_ATTRIBUTE);
          this.createDynamicComponent(
            formName,
            relevantResourceName,
            propertyName,
            propertyKey,
            propertyPath,
            receivedData
          );
        })
    }
    else {
      this.editableView = elem.getAttribute(EDITABLE_VIEW_ATTRIBUTE);
      this.createDynamicComponent(
        formName,
        relevantResourceName,
        propertyName,
        propertyKey,
        propertyPath
      );
    }
  }

  public isActiveRevision(): boolean
  {
    let isCurrentRevisionActive = false;
    let numberOfCurrentRevision = 1;
    const countRevisions = this.testService.patientTests.current.workflowRequest.revisions.length;

    this.testService.patientTests.current.workflowRequest.revisions.forEach((revision: any, idx: number) => {
      if (revision.id === this.currentRevisionId) {
        numberOfCurrentRevision = idx + 1;
      }
    });

    if (this.testService.patientTests.current.workflowRequest.activeRevision === this.currentRevisionId) {
      isCurrentRevisionActive = true;
    }

    // when test has status -test completed in the workflowRequest.activeRevision has None!
    // but the active revision is always the latest revision
    if (this.testService.patientTests.current.workflowRequest.activeRevision.toLowerCase() === 'none' && countRevisions === numberOfCurrentRevision) {
      isCurrentRevisionActive = true;
    }

    return isCurrentRevisionActive;
  }

  private init(testService: TestService) {
    this.currentRevision = testService.patientTests.current.workflowRequest.revisions.filter((revision: any) => {
      if (revision.id === this.currentRevisionId) {
        return revision;
      }
    });
    this.xHtmlReportTemplate = testService.testConfiguration.xHtmlReportTemplates[0];
    this.analysis = testService.patientTests.current;
    this.testNumber = testService.patientTests.current.identifiers[0].value;
    this.revisionDetails = this.getRevisionDetails(testService);
  }

  private getRevisionDetails(testService: TestService): any {
    const revisions = testService.patientTests.current.workflowRequest.revisions;
    const revisionDetails = {
      firstRevisionId: revisions[0].id,
      revisionId: this.testParams.revisionId,
      revisionName: null,
      revisionStatus: null,
    };

    if (this.currentRevision && this.currentRevision.length === 1) {
      revisionDetails.revisionName = this.currentRevision[0].name;
      revisionDetails.revisionStatus = this.currentRevision[0].status;
    }

    return revisionDetails;
  }

  private createDynamicComponent(
    formName: string,
    relevantResourceName: string,
    propertyName: string,
    propertyKey: string,
    propertyPath: string,
    receivedData: any = [],
  ) {
    document.getElementById('modalContainer').style.display = 'block';
    const componentRef = this.dynamicallyAddComponentService.createDynamicComponent(this.getComponentByViewName().value);

    if (this.editableView === 'dataCollection') {
      receivedData[propertyName].map((resourceName: any) => {
        try {
          if (resourceName.name === propertyKey) {
            resourceName.value = JSON.parse(resourceName.value);
          }
        } catch (e) { }
      });

      (<any> componentRef.instance).relevantFormsList = [formName]; // var needs to find additional form in the test config
      (<any> componentRef.instance).formData = _.get(receivedData, propertyPath, {});

      this.updatedDataTable = {
        resourceName: relevantResourceName,
        propertyName: propertyName,
        propertyKey: propertyKey,
        propertyPath: propertyPath,
        originalData: receivedData,
        data: { }
      };

      (<any> componentRef.instance).formDataUpdatedEvent
        .pipe(
          takeUntil(this.destroyed$.asObservable())
        )
        .subscribe((updatedData: any) => {
          this.updatedDataTable.data = updatedData;
        });
    }

    // run changes for Chrome
    this.cdr.detectChanges();
  }


  private upsertResource(): Observable<any> {
    switch (this.payload?.resource?.resourceType && this.payload?.resource?.resourceType.toUpperCase()) {
      case 'DIAGNOSTICSUMMARY':
        return this.testService.upsertDiagnosticSummary(this.payload?.resource, this.currentRevisionId);
      default:
        return Observable.of(this.payload?.resource);
    }
  }

  private hideModal(force = false){
    document.getElementById('modalContainer').style.display = 'none';
    this.editableView = '';

    if (force === true) {
      setTimeout(() => {
        this.submitted = true;
      });

      setTimeout(() => {
        this.submitted = false;
      }, 1000);
    }
  }

  private activateMouseEvents(editable: boolean) {
    const editableElements: HTMLCollectionOf<HTMLElement> = document.getElementsByClassName('editable') as HTMLCollectionOf<HTMLElement>;

    for (let i = 0; i < editableElements.length; i++) {
      editableElements[i].setAttribute('style', (editableElements[i].style.cssText ? editableElements[i].style.cssText : '' ) +(editable ? BORDER : '') + 'background: white; cursor: auto');

      editableElements[i].onpointerout = function () {
        editableElements[i].setAttribute('style', (editableElements[i].style.cssText ? editableElements[i].style.cssText : '' ) +(editable ? BORDER : '') + 'background: white; cursor: auto');
      };

      editableElements[i].onpointerover = function () {
        const styleOver = editable
          ? BORDER + 'background: rgba(255, 219, 31, 0.1); cursor: pointer;'
          : 'background: white; cursor: auto;';
        editableElements[i].setAttribute('style', (editableElements[i].style.cssText ? editableElements[i].style.cssText : '' ) + styleOver);
      };
    }
  }

  private getComponentByViewName() {
    return this.componentsToAddDynamically.find((description: any) => description.key === this.editableView);
  }

  private getEditableAncestor(elem) {
    for (; elem && elem !== document; elem = elem.parentNode) {
      if (!!elem.getAttribute(EDITABLE_VIEW_ATTRIBUTE)) {
        return elem;
      }
    }

    return null;
  }

  private analyseRelatedControlTestStatus(relatedTest: RelatedControlTest, controlSampleStatus: any): boolean {
    if (controlSampleStatus && relatedTest.orders && relatedTest.orders.length > 0) {
      return relatedTest.orders.some(order => order.resultStatus && order.resultStatus !== controlSampleStatus[0].display);
    }

    return false;
  }

  private createFlagForProceedTestStage(): void {
    const proceedData = JSON.parse(localStorage.getItem('test-report-proceed')) || [];

    if (!proceedData.includes(this.testParams.revisionId)) {
      proceedData.push(this.testParams.revisionId);
      localStorage.setItem('test-report-proceed', JSON.stringify(proceedData));
    }
  }

  private removeFlagForProceedTestStage(): void {
    const proceedData = JSON.parse(localStorage.getItem('test-report-proceed')) || [];

    if (proceedData.includes(this.currentRevisionId)) {
      localStorage.setItem('test-report-proceed', JSON.stringify(proceedData.filter((item: any) => item !== this.currentRevisionId)));
    }
  }

  private setPrefilledData(): void {
    this.testSummaryActionsService.getStoredReview(this.currentRevisionId)
      .then((reviews: any) => {
        this.interpretationText = reviews.get('finalReview');
        this.addendumText = reviews.get('addendumReview');
      });
  }

  private loadRenderedReport(): void {
    this.loading = true;
    this.cdr.markForCheck();
    this.setClientDate();

    this.reportPreviewService.renderHTMLReport(
      this.xHtmlReportTemplate,
      this.analysis,
      this.testParams,
      this.testNumber,
      this.revisionDetails
    )
    .pipe(
      takeUntil(this.destroyed$.asObservable())
    )
    .finally(() => {
      if (this.isEditableReport) {
        this.activateMouseEvents(this.editReportToggle);
      }
    })
    .subscribe(
      (renderedHtml: any) => {
        this.loading = false;
        this.coreActionCreatorService.setLoader(false);

        if (renderedHtml) {
          this.renderedReportDiv.nativeElement.innerHTML = this.b64DecodeUnicode(renderedHtml);
        }

        this.cdr.markForCheck();
      }
    )
  }

  private setClientDate(): void {
    this.analysis.localDateTime = this.testService.getLocalDateTime();
    this.analysis.localTime = this.testService.getLocalTime();
  }

  private b64DecodeUnicode(str: any): string {
    return decodeURIComponent(atob(str).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  private onReleaseFinalReport(): void {
    if (!!this.testService.testConfiguration?.skipInterpretationSummary) {
      this.releaseFinalReportModal('', false);
    } else {
      this.modalSubscriber$ = this.modalLoader.popupModal(
        PlaceholderModalComponent,
        {
          title: 'Add interpretation summary',
          componentToLoad: ReleaseReportComponent,
          buttons: {
            approveBtn: 'Yes',
            declineBtn: 'Cancel'
          },
          payload: { },
          approveCallback: (summaryText: string) => {
            this.releaseFinalReportModal(summaryText, false);

            return Observable.of(true);
          },
        },
        ModalTypes.NONE
      )
        .pipe(
          map(response => response.payload)
        )
        .finally(() => {
          if (this.modalSubscriber$) {
            this.modalSubscriber$.unsubscribe();
            this.modalSubscriber$ = null;
          }
        })
        .subscribe();
    }
  }

  private releaseFinalReportModal(interpretationText: string, isStopAction: boolean): void {
    this.modalSubscriber$ = this.modalLoader.popupModal(
      ConfirmModalComponent,
      {
        title: 'Release Final Report',
        content: 'Are you sure you want to release final report?',
        approveBtn: 'OK',
        declineBtn: 'Cancel'
      }
    )
      .pipe(
        map(response => response.payload)
      )
      .finally(() => {
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      })
      .subscribe(
        (response: modalResponse) => {
          if (response === 1) {
            this.interpretationText = interpretationText;
            this.performReleaseFinalReport(isStopAction);
          }
        }
      );
  }

  private performReleaseFinalReport(isStopAction: boolean): void{
    this.loading = true;
    this.cdr.markForCheck();
    this.setClientDate();

    if (!isStopAction) {
      this.createFlagForProceedTestStage();
    }

    this.reportPreviewService.releaseFinalReport(
      this.currentUserId,
      this.xHtmlReportTemplate,
      this.analysis,
      this.revisionDetails,
      this.testNumber,
      this.testParams,
      this.interpretationText
    )
      .pipe(
        switchMap(() => { // render docs in the attachment section
          return this.attachmentsService.getDocuments(this.revisionDetails.revisionId)
            .pipe(
              map((res: any) => {
                this.diagnosticOrderActionsService.setTriggerUpdateAttachments(res.json());
              })
            );
        }),
        switchMap(() => { // send final report to EMR
          if (this.postFinalReportToEmrEnabled) {
            return this.reportPreviewService.postReportToEmr(
              this.testParams.testId,
              this.revisionDetails.revisionId
            )
              .pipe(
                map((res) => {
                  res
                    ? this.notificationMessageService.success('Final report was scheduled for posting to EMR')
                    : this.notificationMessageService.error('Failed posting final report to EMR1');

                  return Observable.of(res);
                }),
                catchError((err) => {
                  console.error('Failed posting final report to EMR : Error : ', err.message);
                  this.loading = false;
                  this.notificationMessageService.error('Failed posting final report to EMR');
                  this.createFlagForProceedTestStage();
                  this.cdr.markForCheck();

                  return Observable.of(err);
                })
              );
          } else {
            return Observable.of(true);
          }
        }),
        switchMap(() => { // switch the test to the final status
          return this.workflowService.performWorkflowStep(
            this.analysis,
            isStopAction ? 'final report for stop action' : 'final report preview',
            '',
            this.revisionDetails.revisionId,
            this.testParams.testId,
            this.interpretationText
          ).then(() => Observable.of(true));
        }),
        switchMap(() => { // waiting for the test completed status
          this.pollJobService.pollingUntilStep(
            this.revisionDetails.revisionId,
            this.testParams.testId,
            this.testService.testConfiguration,
            function () {
              this.workflowService.getWorkflowRequest(
                this.testParams.testId,
                this.revisionDetails.revisionId
              )
                .pipe(
                  map((workflow) => {
                    this.workflowConfigService.setActiveWorkflow(workflow).then();
                  }),
                  switchMap(() => {
                    return this.workflowService.getRevisionResourceMap(
                      this.revisionDetails.revisionId
                    )
                      .pipe(
                        map((resourceMap) => {
                          this.workflowConfigService.setResourcesMap(resourceMap);
                        })
                      );
                  }),
                  switchMap(() => {
                    return this.workflowService.getAllPatientTests(this.testParams)
                      .pipe(
                        map((res: any) => {
                          if (_.get(res, 'allTests', false)) {
                            this.diagnosticOrderActionsService.setAllDiagnosticOrders(res.allTests);
                          }

                          return Observable.of(true);
                        })
                      );
                  }),
                  switchMap(() => {
                    return this.workflowService.patientAnalysisById(this.testParams)
                      .pipe(
                        map((res: any) => {
                          if (_.get(res, 'current', false)) {
                            this.diagnosticOrderActionsService.setDiagnosticOrder(res.current);
                            this.testService.patientTests.current = res.current;
                            this.init(this.testService);
                          }

                          return Observable.of(true);
                        })
                      );
                  }),
                  takeUntil(this.destroyed$.asObservable())
                )
                .subscribe(
                  () => {
                    this.loadRenderedReport();
                    this.dashboardService.loadAllItems();
                    this.isAddendumReportEnabled = true;
                    this.isEditableReport = false;
                    this.testSummaryActionsService.initReview(this.revisionDetails.revisionId);
                  },
                  () => {

                    if (!isStopAction) {
                      this.removeFlagForProceedTestStage();
                    }

                    this.notificationMessageService.error('Unable to release final report')
                  },
                  () => {
                    this.loading = false;
                    this.cdr.markForCheck();
                  }
                );
            }.bind(this),
            'None'
          );

          return Observable.of(true);
        }),
        takeUntil(this.destroyed$.asObservable())
      )
      .subscribe(
        () => { },
        (err) => {
          if (!isStopAction) {
            this.removeFlagForProceedTestStage();
          }

          this.onReleaseFinalReportError(err, (isStopAction) ? 'Final report for stop action is failed' : 'Unable to release final report');
        }
      );
  }

  private onReleaseFinalReportError(err: any, errMessage: any): void {
    this.loading = false;
    // this.createFlagForProceedTestStage();
    this.cdr.markForCheck();
    const errorType = _.get(err, 'data.errorType');

    if (errorType !== 'issueReport.final_report_patient_merged_warn') {
      this.notificationMessageService.error(errMessage);
      return;
    }

    let modalApproveBtn = 'OK';

    if (this.qcWarningConfig) {
      modalApproveBtn = this.qcWarningConfig.approveBtn;
    }

    this.modalSubscriber$ = this.modalLoader.popupModal(
      BasicModalComponent,
      {
        title: 'Warning',
        content: errorType,
        params: { 'patientName': err.data.patientName, 'mergedToMRN': err.data.mergedToMRN },
        approveBtn: modalApproveBtn
      },
      ModalTypes.ALERT
    )
      .finally(() => {
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      })
      .subscribe();
  }

  private releaseAddendumReportModal(summaryText: string): void {
    this.modalSubscriber$ = this.modalLoader.popupModal(
      ConfirmModalComponent,
      {
        title: 'Release Addendum Report',
        content: 'Are you sure you want to release addendum report?',
        approveBtn: 'OK',
        declineBtn: 'Cancel'
      },
      ModalTypes.CUSTOM
    )
      .pipe(
        map(response => response.payload)
      )
      .finally(() => {
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      })
      .subscribe(
        (response: modalResponse) => {
          if (response === 1) {
            this.performAddendumReportReport(summaryText);
          }
        }
      );
  }

  private performAddendumReportReport(summaryText: string): void {
    this.loading = true;
    this.cdr.markForCheck();
    this.setClientDate();

    const addendum = {
      updateText: summaryText,
      clientDate: moment().format('DD MMM yyyy'),
      userId: this.currentUserId,
      reportTemplatesExist: this.reportTemplatesExist
    };

    this.reportPreviewService.releaseAddendumReport(
      this.currentUserId,
      this.xHtmlReportTemplate,
      this.analysis,
      this.revisionDetails,
      this.testNumber,
      this.testParams,
      addendum
    )
      .pipe(
        switchMap(() => { // render docs in the attachment section
          return this.attachmentsService.getDocuments(this.revisionDetails.revisionId)
            .pipe(
              map((res: any) => {
                this.diagnosticOrderActionsService.setTriggerUpdateAttachments(res.json());
              })
            );
        }),
        switchMap(() => {
          if (this.postAddendumReportToEmrEnabled) {
            return this.reportPreviewService.postReportToEmr(
              this.testParams.testId,
              this.revisionDetails.revisionId
            )
              .pipe(
                map((res) => {
                  res
                    ? this.notificationMessageService.success('Addendum report was scheduled for posting to EMR')
                    : this.notificationMessageService.error('Failed posting addendum report to EMR');

                  this.loadRenderedReport();

                  return Observable.of(res);
                }),
                catchError((err) => {
                  console.error('Failed posting addendum report to EMR : Error : ', err.message);
                  this.notificationMessageService.error('Failed posting addendum report to EMR');

                  return Observable.of(err);
                })
              )
          } else {
            this.loadRenderedReport();

            return Observable.of(true);
          }
        }),
        switchMap(() => {
          return this.workflowService.getWorkflowRequest(
            this.testParams.testId,
            this.revisionDetails.revisionId
          )
            .pipe(
              map((workflow) => {
                this.workflowConfigService.setActiveWorkflow(workflow).then();
              }),
              switchMap(() => {
                return this.workflowService.getRevisionResourceMap(
                  this.revisionDetails.revisionId
                )
                  .pipe(
                    map((resourceMap) => {
                      this.workflowConfigService.setResourcesMap(resourceMap);
                    })
                  );
              }),
              switchMap(() => {
                return this.workflowService.getAllPatientTests(this.testParams)
                  .pipe(
                    map((res: any) => {
                      if (_.get(res, 'allTests', false)) {
                        this.diagnosticOrderActionsService.setAllDiagnosticOrders(res.allTests);
                      }

                      return Observable.of(true);
                    })
                  );
              }),
              switchMap(() => {
                return this.workflowService.patientAnalysisById(this.testParams)
                  .pipe(
                    map((res: any) => {
                      if (_.get(res, 'current', false)) {
                        this.diagnosticOrderActionsService.setDiagnosticOrder(res.current);
                      }

                      return Observable.of(true);
                    })
                  );
              })
            )
        }),
        takeUntil(this.destroyed$.asObservable())
      )
      .subscribe(
        () => {
          this.dashboardService.loadAllItems();
          this.testSummaryActionsService.initReview(this.revisionDetails.revisionId);
        },
        () => {
          this.notificationMessageService.error('unable to release addendum report from report preview');
        },
        () => {
          this.loading = false;
          this.cdr.markForCheck();
        }
      );
  }

  private editReportStatus(): boolean {
    return !['stopped', 'test completed'].includes(this.currentRevision[0].status.toLowerCase())
  }

  private stopActionStatus(): boolean {
    return ['stopped'].includes(this.currentRevision[0].status.toLowerCase())
      && _.get(this.testService.testConfiguration, 'stopAction.renderReport', false);
  }

  private addendumReportStatus(): boolean {
    return (
      this.revisionDetails &&
      this.revisionDetails.revisionStatus &&
      this.revisionDetails.revisionStatus.toLowerCase() === 'test completed'
    );
  }
}
